import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface OnfidoVerificationProps {
  userId: string;
  profileId: string;
  onComplete: () => void;
  onSkip: () => void;
}

export default function OnfidoVerification({ userId, profileId, onComplete, onSkip }: OnfidoVerificationProps) {
  const [loading, setLoading] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'in_progress' | 'completed' | 'failed'>('pending');

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const { data: profile } = await supabase
        .from('provider_profiles')
        .select('idv_status, onfido_applicant_id')
        .eq('id', profileId)
        .single();

      if (profile?.idv_status === 'verified') {
        setVerificationStatus('completed');
      } else if (profile?.onfido_applicant_id) {
        setVerificationStatus('in_progress');
      }
    } catch (err) {
      console.error('Error checking verification status:', err);
    }
  };

  const startVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      // In a real implementation, you would:
      // 1. Call your backend to create an Onfido applicant
      // 2. Generate an SDK token
      // 3. Initialize the Onfido SDK
      
      // For now, we'll simulate the process
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get provider profile
      const { data: profile } = await supabase
        .from('provider_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Provider profile not found');

      // TODO: Call backend to create Onfido applicant and get SDK token
      // const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-onfido-applicant`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
      //   },
      //   body: JSON.stringify({
      //     providerId: profile.id,
      //     firstName: profile.first_name,
      //     lastName: profile.last_name,
      //     email: profile.email
      //   })
      // });

      // For demo purposes, show a placeholder
      alert('✅ Onfido verification will be integrated here.\n\nIn production, this will:\n1. Create an Onfido applicant\n2. Generate an SDK token\n3. Launch the Onfido verification flow\n4. Process results via webhook\n\nFor now, you can proceed to the next step.');
      
      setVerificationStatus('in_progress');
      
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Failed to start verification');
    } finally {
      setLoading(false);
    }
  };

  if (verificationStatus === 'completed') {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-checkbox-circle-fill text-green-600 text-4xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Identity Verified! ✅</h2>
          <p className="text-gray-600 mb-8">
            Your identity has been successfully verified. You can now proceed to the next step.
          </p>
          <button
            onClick={onComplete}
            className="px-8 py-3 bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D] text-white rounded-lg hover:from-[#0B3C5D]/90 hover:to-[#0B3C5D]/90 transition-all duration-300 cursor-pointer font-semibold shadow-lg whitespace-nowrap"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <div className="mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#0B3C5D] to-[#0B3C5D] rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-shield-check-line text-white text-3xl"></i>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Identity Verification</h2>
        <p className="text-gray-600 text-center">Verify your identity to ensure trust and safety</p>
      </div>

      {/* Information Section */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 mb-8 border-2 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-white text-2xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">What You'll Need</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-green-600 mt-1"></i>
                <span>A valid government-issued photo ID (passport or driving licence)</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-green-600 mt-1"></i>
                <span>A smartphone or computer with a camera</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-green-600 mt-1"></i>
                <span>5-10 minutes to complete the verification</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-green-50 rounded-xl p-6 mb-8 border-2 border-green-200">
        <div className="flex items-start gap-3">
          <i className="ri-shield-check-line text-green-600 text-2xl mt-0.5"></i>
          <div>
            <h4 className="font-bold text-gray-900 mb-2">Secure & Confidential</h4>
            <p className="text-sm text-gray-700">
              We use Onfido, a trusted third-party verification service. Your personal information is encrypted and securely processed. We never store your ID documents.
            </p>
          </div>
        </div>
      </div>

      {/* Process Steps */}
      <div className="mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Verification Process</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0B3C5D] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              1
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Take a photo of your ID</h4>
              <p className="text-sm text-gray-600">Upload a clear photo of your passport or driving licence</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0B3C5D] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              2
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Take a selfie</h4>
              <p className="text-sm text-gray-600">We'll verify that you match your ID photo</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#0B3C5D] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
              3
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Instant verification</h4>
              <p className="text-sm text-gray-600">Results are usually available within minutes</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-red-800">
            <i className="ri-error-warning-line text-xl"></i>
            <span className="text-sm font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={onSkip}
          disabled={loading}
          className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Skip for Now
        </button>
        <button
          onClick={startVerification}
          disabled={loading}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D] text-white rounded-lg hover:from-[#0B3C5D]/90 hover:to-[#0B3C5D]/90 transition-all duration-300 cursor-pointer font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              <span>Starting...</span>
            </>
          ) : (
            <>
              <i className="ri-shield-check-line"></i>
              <span>Start Verification</span>
            </>
          )}
        </button>
      </div>

      {/* Help Text */}
      <p className="text-center text-sm text-gray-600 mt-6">
        Having trouble? <a href="/contact" className="text-[#0B3C5D] hover:underline font-medium">Contact Support</a>
      </p>
    </div>
  );
}
